// FeatureHighlight - a pinned tooltip that draws attention to a new or
// important element (onboarding, guided tours, feature announcements) and
// remembers being dismissed in localStorage.
//
// Behavioral port of v1 modules/FeatureHighlight.js. v1 built it straight on
// tippy.js; v2 composes our own tooltip (modules/tooltip.ts over
// @floating-ui/dom) with the same defaults: success theme, shown on create,
// manual trigger, close button injected into the content, placement top -
// bottom below the m breakpoint.
//
// HTML contract (unchanged from v1): [data-feature-highlight="<id>"] with the
// content in `title`/`data-tooltip-content` (legacy `data-tippy-content` still
// works) or a `data-html="#template"` reference, optional `data-close-text`,
// plus [data-feature-highlight-reset-all] buttons anywhere on the page.
// localStorage keys are kept byte-for-byte: feature-highlight-<id>-dismissed.

import { BREAKPOINTS } from '../const';
import { SingleTooltip, type TooltipConfig, type TooltipInstance } from './tooltip';

export type FeatureHighlightConfig = TooltipConfig;

const dismissedKey = (id: string): string => `feature-highlight-${id}-dismissed`;

const isDismissed = (id: string): boolean =>
  localStorage.getItem(dismissedKey(id)) === 'true';

/** v1 checked `(max-width: 768px)`; that is the m breakpoint. */
const isNarrow = (): boolean =>
  // matchMedia is unavailable in jsdom (tests).
  typeof window.matchMedia === 'function'
  && window.matchMedia(`(max-width: ${BREAKPOINTS.m}px)`).matches;

const defaultConfig: FeatureHighlightConfig = {
  arrow: true,
  interactive: true,
  allowHTML: true,
  delay: 500,
  theme: 'success',
  showOnCreate: true,
  hideOnClick: false,
  trigger: 'manual',
  // v1 parity: highlights sit below tooltips (9999) in the stacking order.
  zIndex: 999,
};

export default class FeatureHighlight {
  selector: string;

  config: FeatureHighlightConfig;

  highlights: TooltipInstance[] = [];

  constructor(selector = '[data-feature-highlight]', config: FeatureHighlightConfig = {}) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this._init);
    } else {
      this._init();
    }

    document.addEventListener('click', this._onResetClick);
  }

  destroy(): void {
    this.highlights.forEach(highlight => highlight.destroy());
    this.highlights = [];
    // v1 leaked the reset-all listener on every destroy; v2 removes it.
    document.removeEventListener('click', this._onResetClick);
  }

  update(): void {
    this.highlights.forEach(highlight => highlight.destroy());
    this.highlights = [];
    this._init();
  }

  /** Show a highlight by its data-feature-highlight id. */
  showById(id: string): boolean {
    const highlight = this._find(id);
    highlight?.show();

    return Boolean(highlight);
  }

  /** Hide a highlight without remembering the dismissal. */
  hideById(id: string): boolean {
    const highlight = this._find(id);
    highlight?.hide();

    return Boolean(highlight);
  }

  /** Hide a highlight and remember it as dismissed. */
  dismissById(id: string): boolean {
    const highlight = this._find(id);
    if (!highlight) return false;

    highlight.hide();
    localStorage.setItem(dismissedKey(id), 'true');

    return true;
  }

  /** Forget the dismissal of one highlight, so it shows again on next init. */
  resetDismissedState(id: string): boolean {
    if (!id) {
      console.error('FeatureHighlight: id is required');
      return false;
    }

    localStorage.removeItem(dismissedKey(id));
    return true;
  }

  /** Forget every dismissal; returns how many were removed. */
  resetAllDismissedStates(): number {
    const keys = Object.keys(localStorage).filter(
      key => key.startsWith('feature-highlight-') && key.endsWith('-dismissed'),
    );

    keys.forEach(key => localStorage.removeItem(key));

    return keys.length;
  }

  private _find(id: string): TooltipInstance | undefined {
    return this.highlights.find(
      highlight => (highlight.reference as HTMLElement).dataset.featureHighlight === id,
    );
  }

  private _init = (): void => {
    const targets = [...document.querySelectorAll<HTMLElement>(this.selector)];

    this.highlights = targets
      .filter(target => {
        const id = target.dataset.featureHighlight;

        if (!id) {
          console.warn(
            'FeatureHighlight: element is missing a data-feature-highlight id:',
            target,
          );
          return true; // v1 parity: render it anyway, it just cannot be remembered
        }

        return !isDismissed(id);
      })
      .map(
        target =>
          new SingleTooltip(target, {
            ...this.config,
            placement: this.config.placement ?? (isNarrow() ? 'bottom' : 'top'),
            onMount: (box, instance) => {
              this._decorate(box, instance);
              this.config.onMount?.(box, instance);
            },
          }),
      );
  };

  /** Wrap the content and add the close button (v1 did this in tippy onMount). */
  private _decorate(box: HTMLElement, instance: TooltipInstance): void {
    const content = box.querySelector<HTMLElement>('.ms-tooltip__content');
    if (!content) return;
    if (content.classList.contains('feature-highlight-wrapper')) return;

    content.classList.add(
      'feature-highlight-wrapper',
      'd-flex',
      'align-items-start',
      'gap-tiny',
    );

    const inner = document.createElement('div');
    inner.className = 'feature-highlight-content';
    while (content.firstChild) {
      inner.append(content.firstChild);
    }

    const reference = instance.reference as HTMLElement;

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'feature-highlight-close link link--white';
    close.innerHTML = '<i class="far fa-xmark fa-lg"></i>';
    close.setAttribute('title', reference.dataset.closeText || 'Close');

    close.addEventListener('click', () => {
      instance.hide();

      const id = reference.dataset.featureHighlight;
      if (id) localStorage.setItem(dismissedKey(id), 'true');
    });

    content.append(inner, close);
  }

  private _onResetClick = (event: Event): void => {
    const target = (event.target as Element | null)?.closest(
      '[data-feature-highlight-reset-all]',
    );
    if (!target) return;

    event.preventDefault();
    this.resetAllDismissedStates();
    this.update();
  };
}
